import React, { useEffect, useRef, useState, useCallback } from "react";

/**
 * DraggableFloater — wraps any floating UI element so the owner can drag
 * it anywhere on the screen. Position is persisted in localStorage under
 * the supplied storageKey so the placement survives refreshes.
 *
 * Click vs drag is distinguished: if the mouse moved < 4px between
 * pointerdown and pointerup we treat it as a click and forward to the
 * child via the onClick prop. Otherwise we suppress the click so dragging
 * doesn't accidentally fire a navigation.
 *
 * Owner brief (Feb 2026): "always make everything that is added to a
 * screen movable please". This is the single component every new
 * floating button/pill row should use.
 */
export default function DraggableFloater({
  storageKey,
  initial = { right: 12, top: 12 },
  zIndex = 50,
  children,
  className = "",
  testid,
}) {
  // Internal position stored as {left, top} once dragged. Before any
  // drag we honour the `initial` prop (which can use right/bottom for
  // RTL anchoring). After first drag we always use left/top.
  const [pos, setPos] = useState(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) return JSON.parse(raw);
    } catch (_) { /* ignore */ }
    return null;
  });
  const dragRef = useRef(null);
  const stateRef = useRef({
    dragging: false,
    moved: 0,
    startX: 0, startY: 0,
    origLeft: 0, origTop: 0,
  });

  const persist = useCallback((p) => {
    try { localStorage.setItem(storageKey, JSON.stringify(p)); } catch (_) { /* ignore */ }
  }, [storageKey]);

  const onPointerDown = (e) => {
    if (!dragRef.current) return;
    const rect = dragRef.current.getBoundingClientRect();
    stateRef.current = {
      dragging: true, moved: 0, captured: false,
      startX: e.clientX, startY: e.clientY,
      origLeft: rect.left, origTop: rect.top,
      pointerId: e.pointerId,
    };
    // ⚠️ DO NOT setPointerCapture here. If we capture on pointerdown the
    // synthesized click event after pointerup gets redirected to THIS
    // wrapper instead of the inner <button>, which means the button's
    // onClick (e.g. navigate to /trading) never fires. We capture later
    // only AFTER the user actually starts dragging.
  };

  const onPointerMove = (e) => {
    if (!stateRef.current.dragging) return;
    const dx = e.clientX - stateRef.current.startX;
    const dy = e.clientY - stateRef.current.startY;
    stateRef.current.moved = Math.max(stateRef.current.moved, Math.abs(dx) + Math.abs(dy));
    if (stateRef.current.moved < 4) return;
    // First time we cross the drag threshold — grab pointer capture so we
    // get all subsequent moves even if the cursor leaves the element.
    if (!stateRef.current.captured) {
      try { e.currentTarget.setPointerCapture?.(stateRef.current.pointerId); } catch (_) {}
      stateRef.current.captured = true;
    }
    const nextLeft = Math.max(4, Math.min(window.innerWidth  - 16, stateRef.current.origLeft + dx));
    const nextTop  = Math.max(4, Math.min(window.innerHeight - 16, stateRef.current.origTop  + dy));
    setPos({ left: nextLeft, top: nextTop });
  };

  const onPointerUp = (e) => {
    if (!stateRef.current.dragging) return;
    const wasClick = stateRef.current.moved < 4;
    const wasCaptured = stateRef.current.captured;
    stateRef.current.dragging = false;
    stateRef.current.captured = false;
    if (wasCaptured) {
      try { e.currentTarget.releasePointerCapture?.(stateRef.current.pointerId); } catch (_) {}
    }
    if (!wasClick) {
      // suppress the upcoming click so drag doesn't trigger navigation
      e.preventDefault?.();
      e.stopPropagation?.();
      if (pos) persist(pos);
    }
  };

  // Re-apply persisted pos when storageKey changes
  useEffect(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      setPos(raw ? JSON.parse(raw) : null);
    } catch (_) { /* ignore */ }
  }, [storageKey]);

  const style = pos
    ? { position: "fixed", left: pos.left, top: pos.top, zIndex, touchAction: "none" }
    : { position: "fixed", zIndex, touchAction: "none", ...initial };

  return (
    <div
      ref={dragRef}
      style={style}
      className={className}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      data-testid={testid}
    >
      {children}
    </div>
  );
}
